import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError, num } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, X, PencilSimple } from "@phosphor-icons/react";

export default function Users() {
    const { user, agencyId } = useAuth();
    const [users, setUsers] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [open, setOpen] = useState(false);
    const [editUser, setEditUser] = useState(null); // user object being edited
    const [form, setForm] = useState({
        email: "",
        password: "",
        name: "",
        role: "operator",
        agency_id: agencyId || "",
    });
    const canManage = ["owner", "agency_manager"].includes(user?.role);

    const load = () => {
        if (!canManage) return;
        api.get("/users").then((r) => setUsers(r.data));
        api.get("/agencies").then((r) => setAgencies(r.data));
    };

    useEffect(() => {
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setForm((f) => ({ ...f, agency_id: agencyId || f.agency_id }));
    }, [agencyId]);

    if (!canManage) {
        return (
            <div className="p-12 text-center">
                <div className="overline">Accesso riservato</div>
                <p className="mt-2 text-zinc-600">
                    Solo owner e agency manager possono gestire gli utenti.
                </p>
            </div>
        );
    }

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/users", form);
            toast.success("Utente creato");
            setOpen(false);
            setForm({
                email: "",
                password: "",
                name: "",
                role: "operator",
                agency_id: agencyId || "",
            });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare utente?")) return;
        try {
            await api.delete(`/users/${id}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    // Lista delle agenzie effettive di un utente (agency_ids + agency_id, deduplicata)
    const effectiveAgencyIds = (u) => {
        const ids = new Set(u.agency_ids || []);
        if (u.agency_id) ids.add(u.agency_id);
        return Array.from(ids);
    };

    return (
        <div data-testid="users-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <div className="overline mb-2">Team</div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
                        Utenti
                    </h1>
                </div>
                <button
                    onClick={() => setOpen(true)}
                    className="btn-primary flex items-center gap-2"
                    data-testid="add-user-btn"
                >
                    <Plus size={14} weight="bold" /> Nuovo utente
                </button>
            </div>

            <div className="bg-white border border-zinc-200">
                <table className="ft-table">
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Email</th>
                            <th>Ruolo</th>
                            <th>Agenzia</th>
                            <th className="text-right">Tariffa oraria</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => {
                            const userAgIds = effectiveAgencyIds(u);
                            const userAgs = userAgIds
                                .map((id) => agencies.find((a) => a.id === id))
                                .filter(Boolean);
                            return (
                                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                    <td className="font-bold">{u.name}</td>
                                    <td className="text-zinc-600">{u.email}</td>
                                    <td>
                                        <span className="overline">{u.role}</span>
                                    </td>
                                    <td className="text-zinc-600">
                                        {userAgs.length === 0 ? (
                                            "—"
                                        ) : (
                                            <div className="flex flex-wrap gap-1.5">
                                                {userAgs.map((a) => (
                                                    <span
                                                        key={a.id}
                                                        className="inline-flex items-center px-2 py-0.5 bg-zinc-100 border border-zinc-200 text-xs font-bold"
                                                        data-testid={`user-${u.id}-agency-${a.id}`}
                                                    >
                                                        {a.name}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                    <td className="text-right">
                                        {["operator", "agency_manager"].includes(u.role) ? (
                                            <HourlyRateCell
                                                user={u}
                                                canEdit={user.role === "owner"}
                                                onSaved={load}
                                            />
                                        ) : (
                                            <span className="text-zinc-400">—</span>
                                        )}
                                    </td>
                                    <td className="text-right">
                                        <div className="flex items-center justify-end gap-3">
                                            {user.role === "owner" && u.role === "agency_manager" && (
                                                <button
                                                    onClick={() => setEditUser(u)}
                                                    className="text-zinc-400 hover:text-klein"
                                                    aria-label="Modifica utente"
                                                    data-testid={`edit-user-${u.id}`}
                                                >
                                                    <PencilSimple size={14} />
                                                </button>
                                            )}
                                            {u.id !== user.id && (
                                                <button
                                                    onClick={() => remove(u.id)}
                                                    className="text-zinc-400 hover:text-red-600"
                                                    data-testid={`delete-user-${u.id}`}
                                                >
                                                    <Trash size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {open && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-start justify-end"
                    onClick={() => setOpen(false)}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={create}
                        className="w-full max-w-md h-full bg-white border-l border-zinc-200 p-8 overflow-y-auto"
                        data-testid="user-form"
                    >
                        <div className="overline mb-3">Nuovo Utente</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-6">
                            Aggiungi membro.
                        </h2>
                        <label className="block mb-4">
                            <div className="overline mb-2">Nome</div>
                            <input
                                className="inp"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                                data-testid="user-form-name"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Email</div>
                            <input
                                type="email"
                                className="inp"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                required
                                data-testid="user-form-email"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Password</div>
                            <input
                                type="password"
                                className="inp"
                                minLength={6}
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                required
                                data-testid="user-form-password"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Ruolo</div>
                            <select
                                className="inp"
                                value={form.role}
                                onChange={(e) => setForm({ ...form, role: e.target.value })}
                                data-testid="user-form-role"
                            >
                                <option value="operator">Operatore</option>
                                <option value="agency_manager">Agency Manager</option>
                            </select>
                        </label>
                        <label className="block mb-8">
                            <div className="overline mb-2">Agenzia</div>
                            <select
                                className="inp"
                                value={form.agency_id}
                                onChange={(e) => setForm({ ...form, agency_id: e.target.value })}
                                required
                                data-testid="user-form-agency"
                            >
                                {agencies.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                className="btn-primary flex-1"
                                data-testid="user-form-submit"
                            >
                                Crea
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {editUser && (
                <EditUserModal
                    user={editUser}
                    agencies={agencies}
                    onClose={() => setEditUser(null)}
                    onChange={(updated) => {
                        setEditUser(updated);
                        load();
                    }}
                />
            )}
        </div>
    );
}

function HourlyRateCell({ user, canEdit, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(String(user.hourly_rate ?? ""));
    const [saving, setSaving] = useState(false);

    const openEdit = () => {
        setValue(String(user.hourly_rate ?? ""));
        setEditing(true);
    };
    const cancelEdit = () => {
        setValue(String(user.hourly_rate ?? ""));
        setEditing(false);
    };

    const save = async () => {
        const rate = parseFloat(String(value).replace(",", "."));
        if (isNaN(rate) || rate < 0) {
            toast.error("Tariffa non valida");
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/users/${user.id}/hourly-rate`, { hourly_rate: rate });
            toast.success("Tariffa oraria aggiornata");
            setEditing(false);
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const hasRate = user.hourly_rate !== null && user.hourly_rate !== undefined;

    if (!canEdit) {
        return (
            <span className="text-zinc-500" data-testid={`hourly-rate-${user.id}`}>
                {hasRate ? `${num(user.hourly_rate)} €/h` : "—"}
            </span>
        );
    }

    if (!editing) {
        return (
            <button
                onClick={openEdit}
                className="font-bold tabular-nums hover:text-klein"
                data-testid={`hourly-rate-${user.id}`}
                title="Modifica tariffa oraria"
            >
                {hasRate ? `${num(user.hourly_rate)} €/h` : "Imposta €/h"}
            </button>
        );
    }
    return (
        <div className="flex items-center justify-end gap-1">
            <input
                autoFocus
                type="text"
                inputMode="decimal"
                className="inp !w-24 tabular-nums text-right !py-1"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") save();
                    if (e.key === "Escape") cancelEdit();
                }}
                data-testid={`hourly-rate-input-${user.id}`}
            />
            <button
                onClick={save}
                disabled={saving}
                className="btn-secondary !py-1 !px-2 text-xs"
                data-testid={`hourly-rate-save-${user.id}`}
            >
                ✓
            </button>
            <button
                onClick={cancelEdit}
                className="btn-secondary !py-1 !px-2 text-xs"
                data-testid={`hourly-rate-cancel-${user.id}`}
            >
                ✕
            </button>
        </div>
    );
}

function EditUserModal({ user, agencies, onClose, onChange }) {
    const [name, setName] = useState(user.name || "");
    const [savingName, setSavingName] = useState(false);
    const [adding, setAdding] = useState(false);
    const [addAgId, setAddAgId] = useState("");

    useEffect(() => setName(user.name || ""), [user.id, user.name]);

    const currentAgIds = (() => {
        const ids = new Set(user.agency_ids || []);
        if (user.agency_id) ids.add(user.agency_id);
        return Array.from(ids);
    })();
    const currentAgs = currentAgIds
        .map((id) => agencies.find((a) => a.id === id))
        .filter(Boolean);
    const availableAgs = agencies.filter((a) => !currentAgIds.includes(a.id));

    const saveName = async () => {
        const trimmed = name.trim();
        if (!trimmed || trimmed === user.name) return;
        setSavingName(true);
        try {
            const r = await api.patch(`/users/${user.id}`, { name: trimmed });
            toast.success("Nome aggiornato");
            onChange(r.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSavingName(false);
        }
    };

    const addAgency = async () => {
        if (!addAgId) return;
        setAdding(true);
        try {
            const r = await api.post(`/users/${user.id}/assign-agency`, {
                agency_id: addAgId,
            });
            toast.success("Agenzia assegnata");
            setAddAgId("");
            onChange(r.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setAdding(false);
        }
    };

    const removeAgency = async (agId, agName) => {
        if (currentAgs.length <= 1) {
            toast.error("Impossibile rimuovere l'ultima agenzia. Elimina l'utente.");
            return;
        }
        if (!window.confirm(`Rimuovere "${agName}" dalle agenzie gestite?`)) return;
        try {
            const r = await api.post(`/users/${user.id}/unassign-agency`, {
                agency_id: agId,
            });
            toast.success("Agenzia rimossa");
            onChange(r.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 bg-white/80 backdrop-blur-sm z-[100] flex items-start justify-end"
            onClick={onClose}
            data-testid="edit-user-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md h-full bg-white border-l border-zinc-200 p-8 overflow-y-auto"
            >
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="overline mb-2">Modifica utente</div>
                        <h2 className="text-3xl font-black tracking-tighter">
                            {user.name}
                        </h2>
                        <div className="text-xs text-zinc-500 mt-1">{user.email}</div>
                    </div>
                    <button onClick={onClose} aria-label="Chiudi" className="text-zinc-400 hover:text-ink">
                        <X size={18} weight="bold" />
                    </button>
                </div>

                <div className="mt-6">
                    <div className="overline mb-2">Nome</div>
                    <div className="flex gap-2">
                        <input
                            className="inp flex-1"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            data-testid="edit-user-name"
                        />
                        <button
                            type="button"
                            onClick={saveName}
                            disabled={savingName || !name.trim() || name.trim() === user.name}
                            className="btn-secondary"
                            data-testid="edit-user-save-name"
                        >
                            Salva
                        </button>
                    </div>
                </div>

                <div className="mt-8">
                    <div className="overline mb-2">Agenzie gestite</div>
                    {currentAgs.length === 0 ? (
                        <div className="text-sm text-zinc-500">Nessuna agenzia.</div>
                    ) : (
                        <ul className="space-y-2" data-testid="edit-user-agencies">
                            {currentAgs.map((a) => (
                                <li
                                    key={a.id}
                                    className="flex items-center justify-between border border-zinc-200 px-3 py-2"
                                >
                                    <span className="text-sm font-bold">{a.name}</span>
                                    <button
                                        onClick={() => removeAgency(a.id, a.name)}
                                        className="text-zinc-400 hover:text-red-600"
                                        aria-label={`Rimuovi ${a.name}`}
                                        disabled={currentAgs.length <= 1}
                                        data-testid={`edit-user-remove-${a.id}`}
                                        title={currentAgs.length <= 1 ? "Ultima agenzia: elimina l'utente per rimuoverla" : "Rimuovi"}
                                    >
                                        <X size={14} weight="bold" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {availableAgs.length > 0 && (
                    <div className="mt-6">
                        <div className="overline mb-2">Aggiungi agenzia</div>
                        <div className="flex gap-2">
                            <select
                                className="inp flex-1"
                                value={addAgId}
                                onChange={(e) => setAddAgId(e.target.value)}
                                data-testid="edit-user-add-agency-select"
                            >
                                <option value="">Seleziona…</option>
                                {availableAgs.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.name}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={addAgency}
                                disabled={!addAgId || adding}
                                className="btn-primary"
                                data-testid="edit-user-add-agency-btn"
                            >
                                <Plus size={12} weight="bold" /> Aggiungi
                            </button>
                        </div>
                    </div>
                )}

                <div className="mt-10 flex justify-end">
                    <button onClick={onClose} className="btn-secondary" data-testid="edit-user-close">
                        Chiudi
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
